/**
 * DRZEWO KATEGORII ZGŁOSZENIA — JEDNA REGUŁA DLA KAŻDEGO SELECTA KATEGORII W ZGŁOSZENIACH.
 *
 * [polecenie właściciela 2026-09-13 wieczór, S1] „prosiłem by to co jest teraz było podzielone lepiej,
 * czyli sekcja i podkategorie - to wszystko konfigurujemy -> sekcja jest nazwa okna, kategorią przypisane
 * do niej pozycje, jak nie ma w sekcji nazwie okna kategorii to kategoria jest sekcja do wyboru jako typ
 * sprawy.”
 * [S25] „Tworząc nowe zgłoszenie (…) też popraw select kategorii z podziałem na grupy i podgrupy”.
 *
 * CO BYŁO ŹLE: okno zgłoszenia, okno nowego zgłoszenia, okno zamykania i formularz pracownika grupowały
 * kategorie WYŁĄCZNIE po starym polu `isGroup`. Konfigurator (/admin/categories) od 2026-09-01 zapisuje
 * OKNO (`isTile`) i jego kategorie (`parentCategoryId` = okno) z `isGroup: false` — więc każde okno
 * i każda jego kategoria lądowały w jednym płaskim kubełku „Inne”, a okno MAJĄCE kategorie dało się
 * wybrać jako typ sprawy. Ten sam błąd konfigurator naprawił u siebie (CategoriesPage.js, `blokiOkien`),
 * ale druga strona ekranu go nie dostała.
 *
 * REGUŁA (ta sama, co `blokiOkien` w CategoriesPage.js):
 *  1. OKNO z aktywnymi kategoriami = SEKCJA o nazwie okna; wybieralne są jego kategorie, samo okno NIE.
 *  2. OKNO BEZ kategorii = samo jest pozycją do wyboru (sekcja `wybieralna: true`, jedna pozycja = okno).
 *  3. Stare grupy `isGroup` z liśćmi spoza okien = sekcje zgodnościowe (dane sprzed lipca 2026).
 *  4. Reszta = „Inne” (`orphans`).
 * Moduł kategorii okna jest pusty (dziedziczy po oknie) — pozycja dostaje `modulEfektywny`.
 *
 * Kształt wyjścia jest zgodny z dotychczasowym `{ grouped: [{ group, leaves }], orphans }` — dochodzą tylko
 * `wybieralna` przy sekcji oraz `modulEfektywny` i `podpowiedz` przy pozycji. Dzięki temu PoleKategorii
 * i CloseTicketModal nie zmieniają kontraktu.
 *
 * `zachowaj` — identyfikatory, które MAJĄ zostać widoczne mimo reguł (bieżąca kategoria zgłoszenia:
 * wyłączona albo okno, które później dostało kategorie). Bez tego pole kategorii starej sprawy byłoby
 * puste i agent nie wiedziałby, co jest zapisane. Taka pozycja dostaje `podpowiedz`.
 *
 * ⚠ To jest WARSTWA UI. Regułę „okno z kategoriami nie jest typem sprawy” egzekwuje serwer
 * (`KategoriaPrzypisywalna` w DogSoft.Infrastructure/Helpers).
 */

export const PODPOWIEDZ_OKNO_Z_KATEGORIAMI = "okno ma kategorie — wybierz kategorię szczegółową";
export const PODPOWIEDZ_WYLACZONA = "kategoria wyłączona — wybierz aktywną";

const bySort = (a, b) =>
    (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.name || "").localeCompare(String(b.name || ""), "pl");

const takieSame = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * @param {Array} categories lista z GET /categories
 * @param {{ tylkoAktywne?: boolean, zachowaj?: Array }} [opcje]
 * @returns {{ grouped: Array<{group: object, leaves: Array, wybieralna?: boolean}>, orphans: Array }}
 */
export const zbudujDrzewoKategorii = (categories, { tylkoAktywne = true, zachowaj = [] } = {}) => {
    const wszystkie = Array.isArray(categories) ? categories : [];
    const doZachowania = (Array.isArray(zachowaj) ? zachowaj : [zachowaj]).filter((x) => x != null && x !== "");
    const zachowana = (c) => doZachowania.some((id) => takieSame(id, c.id));
    const aktywna = (c) => c.isActive !== false;
    const widoczna = (c) => !tylkoAktywne || aktywna(c) || zachowana(c);
    const zPodpowiedzia = (c, modul, podpowiedz) => ({
        ...c,
        modulEfektywny: c.module || modul || null,
        ...(podpowiedz ? { podpowiedz } : {}),
    });
    const podpowiedzWylaczonej = (c) => (tylkoAktywne && !aktywna(c) ? PODPOWIEDZ_WYLACZONA : null);

    const okna = wszystkie.filter((c) => c.isTile).sort(bySort);
    const reszta = wszystkie.filter((c) => !c.isTile);

    const idDzieciOkien = new Set();
    const sekcjeOkien = [];
    okna.forEach((okno) => {
        const dzieci = reszta.filter((c) => !c.isGroup && takieSame(c.parentCategoryId, okno.id));
        dzieci.forEach((c) => idDzieciOkien.add(String(c.id)));
        const maKategorie = dzieci.some(aktywna);
        const liscie = dzieci
            .filter(widoczna)
            .sort(bySort)
            .map((c) => zPodpowiedzia(c, okno.module, podpowiedzWylaczonej(c)));

        if (maKategorie) {
            // Okno z kategoriami NIE jest typem sprawy — chyba że to zapisana kategoria starego zgłoszenia.
            if (zachowana(okno)) liscie.unshift(zPodpowiedzia(okno, null, PODPOWIEDZ_OKNO_Z_KATEGORIAMI));
            if (liscie.length > 0) sekcjeOkien.push({ group: okno, leaves: liscie });
            return;
        }
        if (!widoczna(okno)) {
            if (liscie.length > 0) sekcjeOkien.push({ group: okno, leaves: liscie });
            return;
        }
        const samoOkno = zPodpowiedzia(okno, null, podpowiedzWylaczonej(okno));
        sekcjeOkien.push(
            liscie.length === 0
                ? { group: okno, leaves: [samoOkno], wybieralna: true }
                : { group: okno, leaves: [samoOkno, ...liscie] }
        );
    });

    const idWGrupach = new Set();
    const sekcjeGrup = reszta
        .filter((c) => c.isGroup)
        .sort(bySort)
        .map((grupa) => {
            const liscie = reszta.filter((c) => !c.isGroup
                && !idDzieciOkien.has(String(c.id))
                && takieSame(c.parentCategoryId, grupa.id));
            liscie.forEach((c) => idWGrupach.add(String(c.id)));
            return {
                group: grupa,
                leaves: liscie.filter(widoczna).sort(bySort)
                    .map((c) => zPodpowiedzia(c, grupa.module, podpowiedzWylaczonej(c))),
            };
        })
        .filter((s) => s.leaves.length > 0);

    const orphans = reszta
        .filter((c) => !c.isGroup && !idDzieciOkien.has(String(c.id)) && !idWGrupach.has(String(c.id)) && widoczna(c))
        .sort(bySort)
        .map((c) => zPodpowiedzia(c, null, podpowiedzWylaczonej(c)));

    return { grouped: [...sekcjeOkien, ...sekcjeGrup], orphans };
};

/** Identyfikatory (jako tekst), które da się wybrać z drzewa — do testów i walidacji po stronie okna. */
export const idWybieralnych = (drzewo) => [
    ...(drzewo?.grouped || []).flatMap((s) => (s.leaves || []).map((l) => String(l.id))),
    ...(drzewo?.orphans || []).map((l) => String(l.id)),
];

export default zbudujDrzewoKategorii;
